import { apiClient } from '../client';

/**
 * Client for `/api/comms/chat/*` — internal DMs + small group threads
 * (Stream E, ADR-027 §3.4). Kept 1:1 with
 * `server/src/routes/comms/chat.routes.ts` +
 * `server/src/modules/comms/chat/chat.service.ts` — mirror, don't invent.
 *
 * Reading a thread is participants-only; unknown ids and other people's
 * threads both 404 `conversation_not_found`. Unread is a per-participant
 * `lastReadAt` watermark, not per-message receipts. Dates cross the wire
 * as ISO strings.
 */

/** Max participants besides the creator (total 2..8 people) — matches the service. */
export const MAX_OTHER_PARTICIPANTS = 7;
/** Messages page size (newest-first keyset pages). */
export const MESSAGE_PAGE_LIMIT = 50;

export interface ChatParticipant {
  membershipId: string;
  firstName: string;
  lastName: string;
}

export interface ChatConversationSummary {
  id: string;
  isGroup: boolean;
  title: string | null;
  lastMessageAt: string | null;
  participants: ChatParticipant[];
}

export interface ChatConversationListItem extends ChatConversationSummary {
  lastMessage: { bodyText: string; senderMembershipId: string | null; createdAt: string } | null;
  unreadCount: number;
}

/** A single message. `bodyText` is null for deleted (tombstone) rows. */
export interface ChatMessage {
  id: string;
  senderMembershipId: string | null;
  bodyText: string | null;
  deletedAt: string | null;
  createdAt: string;
}

/** Narrow a message to its tombstone form (deleted → body-less). */
export function isTombstone(message: ChatMessage): boolean {
  return message.deletedAt !== null || message.bodyText === null;
}

export interface ChatMessagesPage {
  conversation: ChatConversationSummary;
  /** Newest-first — reverse for a bottom-anchored thread. */
  items: ChatMessage[];
  /** More (older) messages exist before this page. */
  hasMore: boolean;
}

export interface CreateConversationInput {
  /** 1..7 OTHER participants (the creator is implicit). */
  participantMembershipIds: string[];
  /** Groups only (ignored for DMs). */
  title?: string;
}

export interface CreateConversationResult {
  conversation: ChatConversationSummary;
  /** True when an existing DM was returned instead of creating a new one. */
  existing: boolean;
}

export interface GetMessagesQuery {
  /** Keyset cursor: a message id — the page returned is strictly older. */
  before?: string;
  limit?: number;
}

const BASE = '/api/comms/chat';

// ── Query keys (shared by the page, components, and invalidations) ───────

export function chatConversationsKey(): readonly ['comms', 'chat', 'conversations'] {
  return ['comms', 'chat', 'conversations'];
}

export function chatThreadKey(conversationId: string): readonly ['comms', 'chat', 'conversation', string] {
  return ['comms', 'chat', 'conversation', conversationId];
}

// ── Endpoints ────────────────────────────────────────────────────────────

export async function listConversations(): Promise<ChatConversationListItem[]> {
  const res = await apiClient.get<{ data: ChatConversationListItem[] }>(`${BASE}/conversations`);
  return res.data.data;
}

export async function createConversation(input: CreateConversationInput): Promise<CreateConversationResult> {
  const res = await apiClient.post<{ data: CreateConversationResult }>(`${BASE}/conversations`, input);
  return res.data.data;
}

export async function getConversationMessages(
  conversationId: string,
  query: GetMessagesQuery = {},
): Promise<ChatMessagesPage> {
  const params = new URLSearchParams();
  if (query.before) params.set('before', query.before);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: ChatMessagesPage }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}/messages${qs}`,
  );
  return res.data.data;
}

export async function sendChatMessage(conversationId: string, bodyText: string): Promise<ChatMessage> {
  const res = await apiClient.post<{ data: ChatMessage }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}/messages`,
    { bodyText },
  );
  return res.data.data;
}

export async function markConversationRead(conversationId: string): Promise<{ lastReadAt: string | null }> {
  const res = await apiClient.post<{ data: { lastReadAt: string | null } }>(
    `${BASE}/conversations/${encodeURIComponent(conversationId)}/read`,
    {},
  );
  return res.data.data;
}

export async function deleteChatMessage(messageId: string): Promise<void> {
  await apiClient.delete(`${BASE}/messages/${encodeURIComponent(messageId)}`);
}
